<template>
  <div class="ols-browse">
    <Teleport to="#top-menu-bar" defer>
      <PageTopBar>
        <template #item-name>
          <span>Browse Ontology Lookup Service (OLS)</span>
        </template>
      </PageTopBar>
    </Teleport>

    <SearchShell
      :is-loading="isLoading"
      :has-searched="hasFetched"
      :result-count="ontologies.length"
      :search-query="searchQuery"
      fill
    >
      <template #filters>
        <div v-if="forCategoryId" class="notification is-info is-light mb-3">
          <span class="icon"><i class="fa fa-sitemap" /></span>
          <span>
            Pick an ontology to add as a source — after importing you will be
            taken back to where you came from.
          </span>
        </div>
        <div
          v-if="!canImportOntology"
          class="notification is-info is-light mb-3"
        >
          <span class="icon"><i class="fa fa-info-circle" /></span>
          <span>
            You do not have permission to import ontologies. Contact your
            administrator to request access.
          </span>
        </div>

        <OlsSearchBar
          v-model="searchQuery"
          placeholder="Search ontologies (e.g. Gene Ontology, CHEBI, OBI)..."
          :loading="isLoading"
          @search="searchOntologies"
        />
      </template>

      <template #loading>
        <ProgressBar />
      </template>

      <template #initial>
        <div class="notification is-light ols-browse__state--initial">
          <span class="icon"><i class="fa fa-book" /></span>
          <span>
            Search for ontologies available in the
            <a
              href="https://www.ebi.ac.uk/ols4/"
              target="_blank"
              rel="noopener"
            >
              Ontology Lookup Service (OLS)
            </a>
            to browse and import them into LabID.
          </span>
        </div>
      </template>

      <template #result-header>
        <span>
          Showing {{ ontologies.length }} of {{ totalElements }} ontologies
          <template v-if="searchQuery.trim()">
            for "{{ searchQuery.trim() }}"
          </template>
          (page {{ currentPage + 1 }} of {{ totalPages }})
        </span>
        <DropdownButton
          :label="`${pageSize} / page`"
          size="small"
          fit-content
          align="end"
        >
          <button
            v-for="opt in PAGE_SIZE_OPTIONS"
            :key="opt"
            class="dd-item"
            :class="{
              'has-text-weight-semibold has-text-primary': opt === pageSize,
            }"
            @click="onPageSizeChange(opt)"
          >
            {{ opt }}
          </button>
        </DropdownButton>
      </template>

      <template #results>
        <div class="ols-grid">
          <div class="ols-grid__header" aria-hidden="true">
            <span>ID</span>
            <span>Name</span>
            <span>Description</span>
            <span>Terms</span>
            <span></span>
          </div>

          <div
            v-for="ontology in ontologies"
            :key="ontology.ontology_id"
            class="ols-grid__row"
            :class="{
              'ols-grid__row--imported': isAlreadyImported(
                ontology.ontology_id,
              ),
            }"
          >
            <div class="ols-grid__cell ols-grid__cell--id">
              <span class="tag is-info is-light">
                {{ ontology.ontology_id }}
              </span>
            </div>

            <div class="ols-grid__cell ols-grid__cell--name">
              <span class="has-text-weight-semibold">
                {{ ontology.name || ontology.ontology_id }}
              </span>
            </div>

            <div class="ols-grid__cell ols-grid__cell--desc">
              <span class="ols-grid__desc-text" :title="ontology.description">
                {{ truncateText(ontology.description, 160) }}
              </span>
            </div>

            <div class="ols-grid__cell ols-grid__cell--terms">
              <span class="ols-grid__label">Terms</span>
              {{ ontology.number_of_terms || '—' }}
            </div>

            <div class="ols-grid__cell ols-grid__cell--action">
              <span
                v-if="isAlreadyImported(ontology.ontology_id)"
                class="tag is-success is-light"
              >
                <span class="icon is-small mr-1">
                  <i class="fa fa-check" />
                </span>
                Imported
              </span>
              <router-link
                v-else-if="canImportOntology"
                :to="{
                  name: 'olsOntologyImport',
                  query: { id: ontology.ontology_id, ...forwardedQuery },
                }"
              >
                <BaseButton
                  size="small"
                  variant="primary"
                  :outlined="true"
                  :icon="['fa', 'fa-download']"
                  label="Import"
                />
              </router-link>
            </div>
          </div>
        </div>
      </template>

      <template #pagination>
        <Pagination
          v-if="ontologies.length > 0"
          :current="currentPage + 1"
          :total="totalPages"
          @page-change="goToPage($event - 1)"
        />
      </template>
    </SearchShell>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { get } from '@/api'
import { hasPrivilege } from '@/auth/authorization'
import { usePageShell } from '@/composables/usePageShell'
import { useUrlSearchState } from '@/composables/useUrlSearchState'
import { useStore } from '@/store'
import { truncateText } from '@/utils'

import OlsSearchBar from '@/components/OlsSearchBar.vue'
import PageTopBar from '@/components/PageTopBar.vue'
import SearchShell from '@/components/SearchShell.vue'

export default {
  name: 'OlsBrowseOntologiesView',
  components: {
    OlsSearchBar,
    PageTopBar,
    SearchShell,
  },
  setup() {
    usePageShell({ title: 'Browse Ontology Lookup Service (OLS)' })
    const store = useStore()
    const route = useRoute()

    // Arrived via "add ontology source" on a category/term: carry the
    // category and the return address through to the import form.
    const forCategoryId = computed(() => route.query.for_category || '')
    const forwardedQuery = computed(() => {
      const query = {}
      if (route.query.for_category)
        query.for_category = route.query.for_category
      if (route.query.back) query.back = route.query.back
      return query
    })

    const {
      query: urlQuery,
      page: urlPage,
      push: pushUrl,
    } = useUrlSearchState()

    const searchQuery = ref('')
    const ontologies = ref([])
    const isLoading = ref(false)
    const hasFetched = ref(false)
    const currentPage = ref(0)
    const PAGE_SIZE_OPTIONS = [15, 30, 60, 100]
    const pageSize = ref(PAGE_SIZE_OPTIONS[0])
    const totalElements = ref(0)
    const totalPages = ref(0)

    const localOntologyCodes = ref(new Set())

    const canImportOntology = computed(() =>
      hasPrivilege('vocabularies', 'ontology', 'add'),
    )

    async function fetchOntologies() {
      isLoading.value = true
      hasFetched.value = true
      try {
        const params = { page: currentPage.value, size: pageSize.value }
        if (searchQuery.value.trim()) params.q = searchQuery.value.trim()
        const { status, data } = await get(
          'vocabularies/ontologies/available-ontologies/',
          { params },
        )
        if (status === 200) {
          ontologies.value = data.ontologies || []
          totalPages.value = data.total_pages || 0
          totalElements.value = data.total_elements || ontologies.value.length
        }
      } catch (error) {
        store.commit('notify', {
          type: 'error',
          message:
            error.response?.data?.error ||
            'Failed to fetch ontologies from OLS',
        })
        ontologies.value = []
      } finally {
        isLoading.value = false
        // store 1-based page in URL; omit when on first page
        pushUrl(
          searchQuery.value.trim(),
          currentPage.value > 0 ? currentPage.value + 1 : 0,
        )
      }
    }

    function searchOntologies() {
      currentPage.value = 0
      fetchOntologies()
    }

    function goToPage(page) {
      if (page >= 0 && page < totalPages.value) {
        currentPage.value = page
        fetchOntologies()
      }
    }

    function onPageSizeChange(size) {
      if (size != null) pageSize.value = size
      currentPage.value = 0
      fetchOntologies()
    }

    async function loadLocalOntologies() {
      try {
        const { status, data } = await get(
          'vocabularies/ontologies/?response_format=flat&page_size=max',
        )
        if (status === 200) {
          const records = data.results || data || []
          localOntologyCodes.value = new Set(
            records.map((r) => r.code?.toLowerCase()).filter(Boolean),
          )
        }
      } catch {
        // non-critical — badges just won't show
      }
    }

    function isAlreadyImported(ontologyId) {
      return localOntologyCodes.value.has(ontologyId?.toLowerCase())
    }

    onMounted(() => {
      if (urlQuery.value) searchQuery.value = urlQuery.value
      if (urlPage.value > 1) currentPage.value = urlPage.value - 1
      fetchOntologies()
      loadLocalOntologies()
    })

    return {
      forCategoryId,
      forwardedQuery,
      searchQuery,
      ontologies,
      isLoading,
      hasFetched,
      currentPage,
      totalElements,
      totalPages,
      canImportOntology,
      truncateText,
      isAlreadyImported,
      PAGE_SIZE_OPTIONS,
      pageSize,
      fetchOntologies,
      searchOntologies,
      goToPage,
      onPageSizeChange,
    }
  },
}
</script>

<style lang="scss" scoped>
.ols-browse {
  height: 100%;
  container-type: size;
  container-name: ols-browse;
}

.ols-browse__state--initial {
  text-align: center;
}

// ── Grid ────────────────────────────────────────────────────
.ols-grid {
  container-type: inline-size;
  container-name: ols-results;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.ols-grid__header {
  display: none; // shown only at wider widths below
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-light);
  padding: var(--space-1) var(--space-3);
}

.ols-grid__row {
  display: grid;
  grid-template-areas:
    'id   name   action'
    'desc desc   action'
    'terms terms terms';
  grid-template-columns: auto 1fr auto;
  gap: var(--space-1) var(--space-3);
  padding: var(--space-3);
  background: var(--bg-default);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  transition:
    background var(--transition-fast),
    border-color var(--transition-fast);

  &--imported {
    background: var(--color-success-light, hsl(141, 53%, 95%));
    border-color: var(--color-success-border, hsl(141, 53%, 80%));
  }

  &:not(&--imported):hover {
    background: var(--bg-subtle);
    border-color: var(--border-default);
  }
}

.ols-grid__cell {
  display: flex;
  align-items: center;

  &--id {
    grid-area: id;
  }
  &--name {
    grid-area: name;
    font-size: 0.9rem;
  }
  &--desc {
    grid-area: desc;
  }
  &--terms {
    grid-area: terms;
    font-size: 0.8rem;
    color: var(--text-light);
    gap: var(--space-1);
  }
  &--action {
    grid-area: action;
    justify-content: flex-end;
  }
}

.ols-grid__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-light);
  margin-right: var(--space-1);
}

.ols-grid__desc-text {
  font-size: 0.8rem;
  color: var(--text-light);
  line-height: 1.4;
}

// Medium: 3-column inline layout — desc moves next to name
@include cq(ols-results, $cq-sm) {
  .ols-grid__header {
    display: grid;
    grid-template-columns: 6rem 1fr 2.5fr 5rem 7rem;
    gap: var(--space-3);
  }

  .ols-grid__row {
    grid-template-areas: 'id name desc terms action';
    grid-template-columns: 6rem 1fr 2.5fr 5rem 7rem;
    align-items: center;
    gap: var(--space-3);
  }

  .ols-grid__cell--terms {
    flex-direction: column;
    align-items: flex-start;
  }

  .ols-grid__label {
    display: none;
  }

  .ols-grid__desc-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

// Wide: more breathing room
@include cq(ols-results, $cq-lg) {
  .ols-grid__header {
    grid-template-columns: 7rem 1.2fr 2.5fr 5rem 8rem;
  }

  .ols-grid__row {
    grid-template-columns: 7rem 1.2fr 2.5fr 5rem 8rem;
  }
}
</style>
